import { FunctionComponent, useState } from 'react';
import { Button, IconButton, Dialog, Table, TableRow, TableCell } from '../../tailwindcomponents';
import { Typography, Input, Snackbar, Select, AutocompleteSelect } from '../../tailwindcomponents';
import { AddCircle, Edit } from '../icons';
import EditColumn from '../../Pages/Security/EditColumn';
import { EmployeeSearch, Collection } from '..';
import { User } from '../../types/user';
import { HandoverIssue, HandoverReportDetails } from '../../types/handover';
import { HandoverReportConfig } from './handoverConfig';
import HandoverMetaCard from './HandoverMetaCard';
import HandoverReportActions from './HandoverReportActions';

type HandoverReportDialogProps = {
    config: HandoverReportConfig,
    allTails: Array<string>,
    report: HandoverReportDetails,
    /** Reloads the open report. */
    refresh: Function,
    onClose: Function,
    /** Reloads the report list behind the dialog. */
    refreshReports: Function,
    isEditMode: Boolean,
    setLoading: Function,
}

/**
 * Body of the handover report dialog, shared by every report-style handover.
 * Read-only unless `isEditMode`.
 */
const HandoverReportDialog: FunctionComponent<HandoverReportDialogProps> = ({
    config, allTails, report, refresh, onClose, refreshReports, isEditMode, setLoading
}) => {
    const api = config.api;
    const [editIndex, setEditIndex] = useState(-1);
    const [selectedTailNumber, setSelectedTailNumber] = useState("");
    const [selectedIssue, setSelectedIssue] = useState("");
    const [areYouSureDialogOpen, setAreYouSureDialogOpen] = useState<boolean>(false);
    const [generalNotes, setGeneralNotes] = useState<string>(report.general_notes);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "warning" | "info" | "success">("success");

    const isToBeReadByEmpty = () => {
        const toBeReadBy = report.to_be_read_by;
        return toBeReadBy === null || toBeReadBy === undefined || toBeReadBy === 0 || toBeReadBy === -1;
    }

    const setSnackBar = (message: string, isSuccess: boolean) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(isSuccess ? "success" : "error");
    }

    /** Runs an edit-mode-only mutation, then reloads the report. */
    const runUpdate = (
        action: () => Promise<{ status: boolean, message: string }>,
        successMessage: string,
        onSuccess?: () => void
    ) => {
        if (!isEditMode) return;
        setLoading(true);
        action().then(res => {
            refresh(report.id);
            setLoading(false);
            if (res.status === true) {
                onSuccess?.();
                setSnackBar(successMessage, true);
            } else {
                setSnackBar(res.message, false);
            }
        });
    }

    const generateTableBody = (row: HandoverIssue, index: number) => {
        return (
            editIndex === index ?
                <TableRow >
                    <TableCell>{index}</TableCell>
                    <TableCell>
                        <AutocompleteSelect
                            width={100}
                            widthPercentage
                            filter_by_text="Tail number"
                            options={allTails}
                            selectedItem={selectedTailNumber}
                            onSelectedItem={(e: any) => setSelectedTailNumber(e.target.value)}
                        />
                    </TableCell>
                    <TableCell>
                        <Input
                            multiline={true}
                            help_text={""}
                            label_text={"Issue"}
                            value={selectedIssue}
                            onTextChange={(text: string) => setSelectedIssue(text)}
                            type="text"
                            width={100}
                        /></TableCell>
                    <EditColumn
                        index={index}
                        saveChanges={() => onModifyIssue(row.id)}
                        deleteRow={() => onDeleteIssue(row.id)}
                        editRow={index}
                        setEditRow={(editRow: number) => { }} />
                </TableRow>
                :
                <TableRow >
                    <TableCell>{index}</TableCell>
                    <TableCell>{row.tail_number}</TableCell>
                    <TableCell>{row.issue}</TableCell>
                    {isEditMode
                        ? <TableCell><IconButton onClick={() => onSelectRow(row, index)}><Edit /></IconButton></TableCell>
                        : <TableCell></TableCell>}
                </TableRow>
        )
    }

    const onSelectRow = (row: HandoverIssue, index: number) => {
        setEditIndex(index);
        setSelectedIssue(row.issue);
        setSelectedTailNumber(row.tail_number);
    }

    const onAddIssue = () => runUpdate(
        () => api.AddIssue(report.id, "", ""),
        "Issue added",
        () => {
            setEditIndex(report.issues_count);
            setSelectedTailNumber("");
            setSelectedIssue("");
        }
    );

    const onDeleteIssue = (issueId: number) => runUpdate(
        () => api.DeleteIssue(issueId, report.id),
        "Issue deleted",
        () => {
            setSelectedTailNumber("");
            setSelectedIssue("");
            setEditIndex(-1);
        }
    );

    const onModifyIssue = (issueId: number) => runUpdate(
        () => api.UpdateIssue(issueId, report.id, selectedIssue, selectedTailNumber),
        "Issue updated",
        () => {
            setSelectedTailNumber("");
            setEditIndex(-1);
        }
    );

    const onAddEmployeeLabour = (user: User | null) => {
        if (!user) return;
        runUpdate(() => api.AddLabour(report.id, user.user_id), "Labour added");
    }

    const onDeleteEmployeeLabour = (labourId: number) =>
        runUpdate(() => api.DeleteLabour(report.id, labourId), "Labour deleted");

    const onAddToBeReadBy = (user: User | null) => {
        if (!user) return;
        runUpdate(() => api.AddToBeReadBy(report.id, user.user_id), "Added to be read by");
    }

    const onSaveGeneralNotes = () =>
        runUpdate(() => api.SaveGeneralNotes(report.id, generalNotes), "General notes saved");

    const onChangeHandoverType = (text: string) => {
        const isDay = text.toLowerCase() === "day";
        if (isDay === report.day) return;
        runUpdate(() => api.ChangeHandoverDayNight(report.id, isDay), "Handover type changed");
    }

    const onFinishReport = () => {
        if (isToBeReadByEmpty()) {
            setSnackBar("Please assign 'To be read by' before finishing the report.", false);
            return;
        }
        setAreYouSureDialogOpen(true);
    }

    const onAreYouSureDialogClicked = (confirmed: boolean) => {
        setAreYouSureDialogOpen(false);
        if (confirmed !== true) return;
        if (!isEditMode || isToBeReadByEmpty()) return;

        setLoading(true);
        api.Close(report.id).then(res => {
            setLoading(false);
            if (res.status !== true) {
                // Stay open: the snackbar below belongs to this dialog, so closing it here would
                // unmount the failure message and leave the still-open report looking closed.
                setSnackBar(res.message, false);
                return;
            }
            setSnackBar("Report closed", true);
            onClose();
            refreshReports();
        });
    }

    return (
        <div>
            <div>
                {(config.showPdfExport === true || config.showEmailReport === true) &&
                    <HandoverReportActions
                        config={config}
                        report={report}
                        isEditingIssue={editIndex !== -1}
                        onResult={(message: string, isSuccess: boolean) => setSnackBar(message, isSuccess)}
                    />
                }
                {isEditMode &&
                    <div className="flex flex-wrap justify-center gap-2">
                        <Button variant="text" onClick={() => onFinishReport()} color='primary'>Finish report</Button>
                    </div>
                }
                <HandoverMetaCard
                    createdBy={report.supervisor_name}
                    createdDate={report.created_date_text}
                    shift={report.shift}
                    isDayShift={report.day}
                    toBeReadBy={report.to_be_read_by_name ?? ''}
                    typeControl={isEditMode
                        ? <Select
                            width={100}
                            widthPercentage
                            options={["Day", "Night"]}
                            selectedItem={report.day === true ? "Day" : "Night"}
                            onSelectedItem={(e: any) => onChangeHandoverType(e.target.value)}
                            filter_by_text={''}
                        />
                        : undefined}
                />
                {isEditMode &&
                    <div className="flex flex-wrap justify-center gap-2">
                        <EmployeeSearch
                            place_holder={"To be read by"}
                            clearOnSelect
                            fullUser={true}
                            onSelectedEmployee={(employee: User | null) => onAddToBeReadBy(employee)}
                            inputWidth={50}
                        />
                    </div>
                }
                <Typography text={'Issues'} align={'center'} variant='h2' />
                <Table
                    headers={["Tail", "Issue", ""]}
                    rows={report.handover_issues ?? []}
                    generateBody={(row: HandoverIssue, index: number) => generateTableBody(row, index)}
                    defaultRowsPerPage={25}
                    pagination={false}
                    skipIndexCol={false}
                />
                {isEditMode &&
                    <div className="flex flex-col items-center">
                        <IconButton onClick={() => onAddIssue()} className="text-success">
                            <AddCircle />
                        </IconButton>
                    </div>
                }
            </div>
            <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                    {isEditMode &&
                        <EmployeeSearch
                            place_holder={config.employeeLabel}
                            clearOnSelect
                            fullUser={true}
                            onSelectedEmployee={(employee: User | null) => onAddEmployeeLabour(employee)}
                            inputWidth={95}
                        />
                    }
                    <Collection
                        edit={isEditMode === true && report.is_submitted === false}
                        background={false}
                        collectionList={report.handover_employees}
                        title={config.employeeLabel}
                        nameProperty="name"
                        idProperty="user_id"
                        removeItem={(itemId: number) => onDeleteEmployeeLabour(itemId)}
                    />
                </div>
            </div>
            <div className="flex flex-row flex-wrap items-center gap-2">
                <Input
                    width={50}
                    multiline={true}
                    help_text={""}
                    label_text={"General notes"}
                    value={generalNotes}
                    onTextChange={(text: string) => setGeneralNotes(text)}
                    type="text"
                    disabled={!isEditMode}
                />
                {isEditMode &&
                <div className='mt-5'>
                        <Button size='small' variant="contained" onClick={() => onSaveGeneralNotes()}>Save notes</Button>
                </div>
                }
            </div>
            <Dialog
                visible={areYouSureDialogOpen}
                max_width={"md"}
                title={"Are you sure you want to finish this report"}
                context={<div></div>}
                onClose={(status: any) => onAreYouSureDialogClicked(status)}
                strict={false}
                show_options={true}
            />
            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition='right'
                severity={snackbarSeverity}
            >
            </Snackbar>
        </div>
    );
};

export default HandoverReportDialog;
